// Tiny, dependency-free Markdown → HTML renderer for event details documents.
// We escape all HTML first, so the output never contains author-supplied raw HTML —
// only the tags this renderer emits. URLs in links are restricted to http(s)/mailto
// and site-relative/anchor targets. The result is meant for use with Lit's unsafeHTML.
//
// Supported: ATX headings, **bold**/__bold__, *italic*/_italic_, `inline code`,
// fenced ``` code blocks, > blockquotes, -/*/+ and ordered lists, GFM pipe tables,
// --- rules, links, and paragraphs. A readable subset, not full CommonMark.

const C0 = String.fromCharCode(0); // NUL sentinel — cannot occur in HTML-escaped text

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function safeUrl(raw: string): string | null {
  const url = raw.trim();
  if (/^(https?:\/\/|mailto:)/i.test(url)) return url;
  if (url.startsWith('/') || url.startsWith('#')) return url;
  return null; // reject javascript:, data:, etc.
}

// --- GFM pipe-table helpers ---
// Split a table row into trimmed cells, tolerating optional leading/trailing pipes.
function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

// A separator row is all cells of the form ---, :---, ---:, or :---:.
function isSeparatorRow(line: string): boolean {
  if (!line.includes('-')) return false;
  const cells = splitRow(line);
  return cells.length > 0 && cells.every((c) => /^:?-+:?$/.test(c));
}

function cellAlign(sep: string): string {
  const l = sep.startsWith(':');
  const r = sep.endsWith(':');
  if (l && r) return 'center';
  if (r) return 'right';
  if (l) return 'left';
  return '';
}

// Inline formatting on already-escaped text. Code spans are pulled out first (replaced
// with C0-delimited placeholders that cannot occur in the escaped text, so they never
// collide with ordinary content like a number surrounded by spaces) and restored last.
function inline(text: string): string {
  const codes: string[] = [];
  let s = text.replace(/`([^`]+)`/g, (_m, code) => {
    codes.push(`<code>${code}</code>`);
    return C0 + (codes.length - 1) + C0;
  });

  s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, label, url) => {
    const safe = safeUrl(url);
    return safe ? `<a href="${safe}" target="_blank" rel="noopener noreferrer">${label}</a>` : label;
  });
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*])\*([^*\s][^*]*)\*/g, '$1<em>$2</em>');
  s = s.replace(/(^|[^_])_([^_\s][^_]*)_/g, '$1<em>$2</em>');

  s = s.replace(new RegExp(C0 + '(\\d+)' + C0, 'g'), (_m, i) => codes[Number(i)] ?? '');
  return s;
}

export function renderMarkdown(src: string): string {
  if (!src) return '';
  const lines = escapeHtml(src.replace(/\r\n?/g, '\n')).split('\n');
  const out: string[] = [];
  let i = 0;
  let para: string[] = [];
  let listType: 'ul' | 'ol' | null = null;

  const flushPara = () => {
    if (para.length) { out.push(`<p>${inline(para.join(' '))}</p>`); para = []; }
  };
  const closeList = () => { if (listType) { out.push(`</${listType}>`); listType = null; } };

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block.
    const fence = line.match(/^\s*```/);
    if (fence) {
      flushPara(); closeList();
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) { body.push(lines[i]); i++; }
      i++; // skip closing fence
      out.push(`<pre><code>${body.join('\n')}</code></pre>`);
      continue;
    }

    // Blank line — paragraph / list break.
    if (!line.trim()) { flushPara(); closeList(); i++; continue; }

    // GFM pipe table: a header row immediately followed by a separator row.
    if (line.includes('|') && i + 1 < lines.length && isSeparatorRow(lines[i + 1])) {
      flushPara(); closeList();
      const headers = splitRow(line);
      const aligns = splitRow(lines[i + 1]).map(cellAlign);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim() && lines[i].includes('|')) { rows.push(splitRow(lines[i])); i++; }
      const al = (n: number) => (aligns[n] ? ` style="text-align:${aligns[n]}"` : '');
      const head = headers.map((h, n) => `<th${al(n)}>${inline(h)}</th>`).join('');
      const body = rows.map((r) => `<tr>${headers.map((_h, n) => `<td${al(n)}>${inline(r[n] ?? '')}</td>`).join('')}</tr>`).join('');
      out.push(`<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`);
      continue;
    }

    // Horizontal rule.
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) { flushPara(); closeList(); out.push('<hr />'); i++; continue; }

    // Heading.
    const h = line.match(/^\s*(#{1,6})\s+(.*)$/);
    if (h) { flushPara(); closeList(); const lvl = h[1].length; out.push(`<h${lvl}>${inline(h[2].trim())}</h${lvl}>`); i++; continue; }

    // Blockquote.
    const bq = line.match(/^\s*>\s?(.*)$/);
    if (bq) {
      flushPara(); closeList();
      const quote: string[] = [bq[1]];
      i++;
      while (i < lines.length && /^\s*>\s?(.*)$/.test(lines[i])) { quote.push(lines[i].replace(/^\s*>\s?/, '')); i++; }
      out.push(`<blockquote>${inline(quote.join(' '))}</blockquote>`);
      continue;
    }

    // List items.
    const ul = line.match(/^\s*[-*+]\s+(.*)$/);
    const ol = line.match(/^\s*\d+\.\s+(.*)$/);
    if (ul || ol) {
      flushPara();
      const want: 'ul' | 'ol' = ul ? 'ul' : 'ol';
      if (listType !== want) { closeList(); out.push(`<${want}>`); listType = want; }
      out.push(`<li>${inline((ul ? ul[1] : ol![1]).trim())}</li>`);
      i++; continue;
    }

    // Otherwise: paragraph text.
    closeList();
    para.push(line.trim());
    i++;
  }
  flushPara(); closeList();
  return out.join('\n');
}
